import Swal from "sweetalert2";

/**
 * Quote status checks and confirmation-dialog actions shared by the reservations
 * module (EditTransfers) and the shared ReservationRowActions component.
 */

/**
 * Determine if a quote can be cancelled based on its status.
 * @param {string} status - The status of the quote.
 * @returns {boolean} - True if the quote can be cancelled, false otherwise.
 */
export function canCancelQuote(status) {
  return status === "NEW" || status === "DRAFT";
}

/**
 * Determine if a quote can be converted based on its status.
 * @param {string} status - The status of the quote.
 * @returns {boolean} - True if the quote can be converted, false otherwise.
 */
export function canConvertQuote(status) {
  return status === "NEW";
}

/**
 * Determine if a quote PDF can be downloaded based on its status.
 * @param {string} status - The status of the quote.
 * @returns {boolean} - True if the quote PDF can be downloaded, false otherwise.
 */
export function canDownloadQuotePDF(status) {
  return status === "NEW";
}

/**
 * Determine if a quote can be duplicated based on its status.
 * @param {string} status - The status of the quote.
 * @returns {boolean} - True if the quote can be duplicated, false otherwise.
 */
export function canDuplicateQuote(status) {
  return status === "NEW" || status === "DRAFT";
}

/** Delete a quote with a SweetAlert2 dialog.
 * @param {string} quoteId - The ID of the quote to delete.
 * @param {function} callback - The callback function to execute after deletion.
 */
export async function deleteQuote(quoteId, callback) {
  let { value } = await Swal.fire({
    title: "Delete Quote",
    html: "Are you sure you want to <strong>Delete</strong> this Quote?",
    imageUrl: require("rhomtrip_assets/src/img/icons/alerts/warning.png"),
    buttonsStyling: false,
    showCloseButton: true,
    showCancelButton: true,
    focusConfirm: false,
    reverseButtons: true,
    cancelButtonText: "No",
    confirmButtonText: "Yes",
    onClose: () => {
      setTimeout(() => { }, 1500);
    }
  });
  if (value) {
    await callback(quoteId);
  }
}

/** Cancel a quote with a SweetAlert2 dialog.
 * @param {string} quoteId - The ID of the quote to cancel.
 * @param {function} callback - The callback function to execute after cancellation.
 */
export async function cancelQuote(quoteId, callback) {
  const { value } = await Swal.fire({
    title: "Cancel Quote",
    imageUrl: require("rhomtrip_assets/src/img/icons/alerts/question.png"),
    html: "Are you sure you want to <strong>Cancel</strong> this Quote?",
    focusConfirm: false,
    buttonsStyling: false,
    showCloseButton: true,
    showCancelButton: true,
    reverseButtons: true,
    allowOutsideClick: false,
    cancelButtonText: "No",
    confirmButtonText: "Yes",
    onClose: () => {
      setTimeout(() => { }, 1500);
    }
  });
  if (value) {
    await callback(quoteId);
  }
}

/** Duplicate a quote with a SweetAlert2 dialog.
 * @param {*} quoteId - The ID of the quote to duplicate.
 * @param {*} callback - The callback function to execute after duplication.
 */
export async function duplicateQuote(quoteId, callback) {
  let { value } = await Swal.fire({
    title: "Duplicate Quote",
    html: "Are you sure you want to <strong>duplicate</strong> this Quote?",
    imageUrl: require("rhomtrip_assets/src/img/icons/alerts/question.png"),
    buttonsStyling: false,
    showCloseButton: true,
    showCancelButton: true,
    focusConfirm: false,
    reverseButtons: true,
    cancelButtonText: "No",
    confirmButtonText: "Yes",
    onClose: () => {
      setTimeout(() => { }, 1500);
    }
  });
  if (value) {
    await callback(quoteId, true);
  }
}

/** Download a quote as a PDF with a SweetAlert2 dialog.
 * @param {*} quoteId - The ID of the quote to download.
 * @param {*} callback - The callback function to execute after downloading.
 */
export async function downloadQuotePDF(quoteId, quoteNumber, callback) {
  let { value } = await Swal.fire({
    title: "Download Quote",
    html: "Are you sure you want to <strong>download</strong> this Quote?",
    imageUrl: require("rhomtrip_assets/src/img/icons/alerts/question.png"),
    buttonsStyling: false,
    showCloseButton: true,
    showCancelButton: true,
    focusConfirm: false,
    reverseButtons: true,
    cancelButtonText: "No",
    confirmButtonText: "Yes",
    onClose: () => {
      setTimeout(() => { }, 1500);
    }
  });
  if (value) {
    await callback(quoteId, quoteNumber);
  }
}

/**
 * Convert a quote to a booking with a SweetAlert2 dialog.
 * @param {*} quoteId - The ID of the quote to convert.
 * @param {*} callback - The callback function to execute after confirmation.
 */
export async function convertToBooking(quoteId, callback) {
  let { value } = await Swal.fire({
    html:
      "Are you sure you want to <strong>convert</strong> this quote to a booking?",
    title: "Convert to Booking",
    imageUrl: require("rhomtrip_assets/src/img/icons/alerts/question.png"),
    buttonsStyling: false,
    showCloseButton: true,
    showCancelButton: true,
    focusConfirm: false,
    reverseButtons: true,
    cancelButtonText: "No",
    confirmButtonText: "Yes",
    showLoaderOnConfirm: true,
    timerProgressBar: true,
    onClose: () => {
      setTimeout(() => { }, 1500);
    }
  });
  if (value) {
    await callback(quoteId);
  }
}
